import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getEmailSettings, updateEmailSettings, uploadEmailBanner, getQuoteNoteTemplates, createQuoteNoteTemplate, updateQuoteNoteTemplate, deleteQuoteNoteTemplate, getEmailLog } from '../services/api';
import { toast } from 'sonner';
import { Loader2, Upload, Plus, Edit, Trash2, X, Check } from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';
import DOMPurify from 'dompurify';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'quote_template', label: 'Quote Template' },
  { id: 'invoice_template', label: 'Invoice Template' },
  { id: 'deposit_invoice_template', label: 'Deposit Invoice Template' },
  { id: 'signature', label: 'Email Signature' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'quote_note_templates', label: 'Quote Note Templates' },
  { id: 'send_log', label: 'Send Log' },
];

const TEMPLATE_VARS = [
  '{{ClientContactPersonFirstName}}', '{{ClientFirstLastName}}', '{{ClientContactPersonEmail}}',
  '{{PropertyManagerEmail}}', '{{HomeOwnerEmail}}', '{{TenantEmail}}', '{{ProperyAddress}}', '{{PropertyAddress}}', '{{JobTitles}}', '{{QuoteNumber}}', '{{AcceptanceLink}}'
];

export default function EmailSettings() {
  const [activeTab, setActiveTab] = useState('general');
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useQuery({ queryKey: ['emailSettings'], queryFn: () => getEmailSettings().then(r => r.data) });

  const saveMutation = useMutation({
    mutationFn: ({ section, data }) => updateEmailSettings(section, data),
    onSuccess: () => { toast.success('Settings updated'); queryClient.invalidateQueries({ queryKey: ['emailSettings'] }); },
    onError: () => toast.error('Failed to save settings')
  });

  if (isLoading) return <div className="flex items-center justify-center py-12"><Loader2 size={32} className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="email-settings-page">
      <h1 className="text-2xl font-bold" data-testid="email-settings-title">Email Setting</h1>
      <div className="bg-white rounded-lg shadow-sm border">
        {/* Tab Nav */}
        <div className="border-b flex" data-testid="email-settings-tabs">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${tab.id}`}
            >{tab.label}</button>
          ))}
        </div>
        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'general' && <GeneralTab settings={settings?.general} onSave={(d) => saveMutation.mutate({ section: 'general', data: d })} saving={saveMutation.isPending} />}
          {activeTab === 'quote_template' && <QuoteTemplateTab settings={settings?.quote_template} onSave={(d) => saveMutation.mutate({ section: 'quote_template', data: d })} saving={saveMutation.isPending} />}
          {activeTab === 'invoice_template' && <InvoiceTemplateTab settings={settings?.invoice_template} onSave={(d) => saveMutation.mutate({ section: 'invoice_template', data: d })} saving={saveMutation.isPending} />}
          {activeTab === 'deposit_invoice_template' && <InvoiceTemplateTab settings={settings?.deposit_invoice_template} onSave={(d) => saveMutation.mutate({ section: 'deposit_invoice_template', data: d })} saving={saveMutation.isPending} isDeposit />}
          {activeTab === 'signature' && <SignatureTab settings={settings?.signature} onSave={(d) => saveMutation.mutate({ section: 'signature', data: d })} saving={saveMutation.isPending} />}
          {activeTab === 'notifications' && <NotificationsTab settings={settings?.notifications} onSave={(d) => saveMutation.mutate({ section: 'notifications', data: d })} saving={saveMutation.isPending} />}
          {activeTab === 'quote_note_templates' && <QuoteNoteTemplatesTab />}
          {activeTab === 'send_log' && <SendLogTab />}
        </div>
      </div>
    </div>
  );
}

// ============ Tab 1: General (SMTP Settings) ============
function GeneralTab({ settings, onSave, saving }) {
  const [form, setForm] = useState({
    display_name: '', email: '', smtp_username: '', password: '', cc: '', bcc: '', smtp: '', port: '25', ssl: false,
  });
  useEffect(() => { if (settings) setForm(prev => ({ ...prev, ...settings })); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [settings]);

  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };
  const F = (label, field, type = 'text') => (
    <div className="flex items-center gap-4 py-3 border-b border-slate-100" key={field}>
      <label className="w-40 text-right text-sm text-slate-600 flex-shrink-0">{label}</label>
      <input type={type} value={form[field] ?? ''} onChange={e => setForm({ ...form, [field]: e.target.value })}
        className="flex-1 px-3 py-2 border-b border-slate-300 text-sm focus:outline-none focus:border-primary bg-transparent"
        data-testid={`general-${field}`} />
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto" data-testid="general-form">
      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">SMTP (Outgoing)</h3>
      {F('Display Name', 'display_name')}
      {F('Email', 'email', 'email')}
      {F('SMTP Username', 'smtp_username')}
      {F('Password', 'password', 'password')}
      {F('CC', 'cc')}
      {F('BCC', 'bcc')}
      {F('SMTP', 'smtp')}
      {F('Port', 'port')}
      <div className="flex items-center gap-4 py-3 border-b border-slate-100">
        <label className="w-40 text-right text-sm text-slate-600 flex-shrink-0">SSL</label>
        <input type="checkbox" checked={form.ssl} onChange={e => setForm({ ...form, ssl: e.target.checked })} className="w-4 h-4" data-testid="general-ssl" />
      </div>

      <div className="flex justify-end pt-4">
        <button type="submit" disabled={saving} className="px-5 py-2 bg-cyan-500 text-white text-sm rounded hover:bg-cyan-600 disabled:opacity-50" data-testid="general-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Update'}
        </button>
      </div>
    </form>
  );
}

// ============ Template Variables Bar ============
function VariablesBar() {
  const copyVar = (v) => { navigator.clipboard.writeText(v); toast.success(`Copied ${v}`); };
  return (
    <div className="bg-amber-50 border border-amber-200 rounded p-4 mb-6" data-testid="variables-bar">
      <div className="text-xs font-bold text-slate-700 mb-2">ACCEPTED VARIABLES</div>
      <div className="flex flex-wrap gap-2">
        {TEMPLATE_VARS.map(v => (
          <button type="button" key={v} onClick={() => copyVar(v)}
            className="px-2.5 py-1 text-xs bg-amber-200 text-slate-800 rounded cursor-pointer hover:bg-amber-300 font-medium"
            data-testid={`var-${v}`}
          >{v}</button>
        ))}
      </div>
    </div>
  );
}

// ============ Tab 2: Quote Email Template ============
function QuoteTemplateTab({ settings, onSave, saving }) {
  const [form, setForm] = useState({
    recipient1: '{{PropertyManagerEmail}}', recipient2: '{{ClientContactPersonEmail}}', recipient3: '',
    subject: 'Quote for {{PropertyAddress}}',
    message: '<p>Good morning {{ClientContactPersonFirstName}},</p><p>Thank you for the opportunity to provide pricing for the {{JobTitles}} at {{PropertyAddress}}.</p><p>Your quotation is attached.</p><p>Please feel free to contact me directly if you have any questions.</p>',
    include_attachments: true
  });
  useEffect(() => { if (settings) setForm(prev => ({ ...prev, ...settings })); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [settings]);

  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };
  return (
    <form onSubmit={handleSubmit} data-testid="quote-template-form">
      <VariablesBar />
      <div className="max-w-2xl mx-auto space-y-4">
        <FormRow label="Recipient 1"><input type="text" value={form.recipient1} onChange={e => setForm({ ...form, recipient1: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm" data-testid="quote-recipient1" /></FormRow>
        <FormRow label="Recipient 2"><input type="text" value={form.recipient2} onChange={e => setForm({ ...form, recipient2: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm" data-testid="quote-recipient2" /></FormRow>
        <FormRow label="Recipient 3"><input type="text" value={form.recipient3} onChange={e => setForm({ ...form, recipient3: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm" data-testid="quote-recipient3" /></FormRow>
        <FormRow label="Subject"><input type="text" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm" data-testid="quote-subject" /></FormRow>
        <div>
          <label className="block text-sm text-slate-600 mb-1">Message</label>
          <RichTextEditor value={form.message} onChange={val => setForm({ ...form, message: val })} />
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-600">Attachments:</label>
          <input type="checkbox" checked={form.include_attachments} onChange={e => setForm({ ...form, include_attachments: e.target.checked })} className="w-4 h-4" data-testid="quote-attachments" />
        </div>
        <div className="flex justify-end pt-4">
          <button type="submit" disabled={saving} className="px-5 py-2 bg-cyan-500 text-white text-sm rounded hover:bg-cyan-600 disabled:opacity-50" data-testid="quote-template-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : 'Update'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ============ Tab 3: Invoice Email Template ============
function InvoiceTemplateTab({ settings, onSave, saving, isDeposit }) {
  const defaultMsg = isDeposit 
    ? '<p>Hello {{ClientContactPersonFirstName}},</p><p>Thank you for accepting our quote for {{PropertyAddress}}.</p><p>Please find attached the deposit invoice for the upcoming works. Payment of this deposit is required to confirm your booking and schedule the work.</p><p>Please feel free to contact me directly if you have any questions.</p>'
    : '<p>Hello {{ClientContactPersonFirstName}},</p><p>Works been completed at this property. Your invoice with payment details is attached.</p><p>Please keep in touch if there is any other property maintenance or painting requirements you need in the future.</p>';
  const defaultSubject = isDeposit ? 'Deposit Invoice for {{PropertyAddress}}' : 'Invoice for {{PropertyAddress}}';
  const prefix = isDeposit ? 'deposit-invoice' : 'invoice';
  const [form, setForm] = useState({
    recipient: '{{ClientContactPersonEmail}}',
    subject: defaultSubject,
    message: defaultMsg,
    include_attachments: true
  });
  useEffect(() => { if (settings) setForm(prev => ({ ...prev, ...settings })); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [settings]);

  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };
  return (
    <form onSubmit={handleSubmit} data-testid={`${prefix}-template-form`}>
      <VariablesBar />
      <div className="max-w-2xl mx-auto space-y-4">
        <FormRow label="Recipient"><input type="text" value={form.recipient} onChange={e => setForm({ ...form, recipient: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm" data-testid={`${prefix}-recipient`} /></FormRow>
        <FormRow label="Subject"><input type="text" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm" data-testid={`${prefix}-subject`} /></FormRow>
        <div>
          <label className="block text-sm text-slate-600 mb-1">Message</label>
          <RichTextEditor value={form.message} onChange={val => setForm({ ...form, message: val })} />
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-600">Attachments:</label>
          <input type="checkbox" checked={form.include_attachments} onChange={e => setForm({ ...form, include_attachments: e.target.checked })} className="w-4 h-4" data-testid={`${prefix}-attachments`} />
        </div>
        <div className="flex justify-end pt-4">
          <button type="submit" disabled={saving} className="px-5 py-2 bg-cyan-500 text-white text-sm rounded hover:bg-cyan-600 disabled:opacity-50" data-testid={`${prefix}-template-save`}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : 'Update'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ============ Tab 4: Email Signature ============
function SignatureTab({ settings, onSave, saving }) {
  const [form, setForm] = useState({
    banner_url: '', registered_painting_licence: '',
    website_link: '', instagram_link: '', facebook_link: ''
  });
  const [uploading, setUploading] = useState(false);
  useEffect(() => { if (settings) setForm(prev => ({ ...prev, ...settings })); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [settings]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadEmailBanner(fd);
      setForm(prev => ({ ...prev, banner_url: res.data.url }));
      toast.success('Banner uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };
  return (
    <form onSubmit={handleSubmit} className="max-w-3xl mx-auto" data-testid="signature-form">
      {/* Banner */}
      <div className="flex gap-6 mb-6">
        <label className="w-44 text-right text-sm text-slate-600 flex-shrink-0 pt-2">Banner</label>
        <div className="flex-1">
          {form.banner_url ? (
            <div className="mb-2">
              <img src={form.banner_url} alt="Banner" className="max-w-md rounded shadow-sm" data-testid="signature-banner-img" />
            </div>
          ) : (
            <div className="w-full max-w-md h-48 bg-slate-100 rounded flex items-center justify-center text-slate-400 text-sm">No banner uploaded</div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 text-sm rounded cursor-pointer hover:bg-slate-300 mt-2">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Change Image
            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="signature-banner-upload" />
          </label>
        </div>
      </div>
      {/* Fields */}
      <FormRow label="Registered Painting Licence"><input type="text" value={form.registered_painting_licence} onChange={e => setForm({ ...form, registered_painting_licence: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm" data-testid="signature-licence" /></FormRow>
      <FormRow label="Website Link"><input type="url" value={form.website_link} onChange={e => setForm({ ...form, website_link: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm" data-testid="signature-website" /></FormRow>
      <FormRow label="Instagram Page Link"><input type="url" value={form.instagram_link} onChange={e => setForm({ ...form, instagram_link: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm" data-testid="signature-instagram" /></FormRow>
      <FormRow label="Facebook Page Link"><input type="url" value={form.facebook_link} onChange={e => setForm({ ...form, facebook_link: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm" data-testid="signature-facebook" /></FormRow>
      <div className="flex justify-end pt-6">
        <button type="submit" disabled={saving} className="px-5 py-2 bg-cyan-500 text-white text-sm rounded hover:bg-cyan-600 disabled:opacity-50" data-testid="signature-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Update'}
        </button>
      </div>
    </form>
  );
}

// ============ Tab 5: Notifications ============
function NotificationsTab({ settings, onSave, saving }) {
  const [form, setForm] = useState({
    approved_message: '<p>Hello,</p><p>Thank you for your quote acceptance.</p><p>Someone from the office will be in touch shortly.</p><p>Kind Regards</p>',
    declined_message: '<p>Hello,</p><p>Thank you for letting us know you do not wish to proceed with the quote.</p><p>We will remove it from our records.</p><p>Kind Regards</p>'
  });
  const [ready, setReady] = useState(false);
  useEffect(() => { if (settings) setForm(prev => ({ ...prev, ...settings })); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [settings]);
  useEffect(() => { const t = setTimeout(() => setReady(true), 100); return () => clearTimeout(t); }, []);

  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };
  return (
    <form onSubmit={handleSubmit} data-testid="notifications-form">
      <div className="bg-cyan-50 border border-cyan-200 rounded p-3 mb-6 text-center text-sm text-slate-600" data-testid="notifications-info">
        System will notify the client and CC'd email on the General setting tab when client declined or approved a quote.
      </div>
      <div className="space-y-6 max-w-3xl mx-auto">
        <div>
          <label className="block text-sm text-slate-600 mb-2">Approved notification Message</label>
          {ready && <RichTextEditor key="approved" value={form.approved_message} onChange={val => setForm(prev => ({ ...prev, approved_message: val }))} />}
        </div>
        <div>
          <label className="block text-sm text-slate-600 mb-2">Declined notification Message</label>
          {ready && <RichTextEditor key="declined" value={form.declined_message} onChange={val => setForm(prev => ({ ...prev, declined_message: val }))} />}
        </div>
        <div className="flex justify-end pt-4">
          <button type="submit" disabled={saving} className="px-5 py-2 bg-cyan-500 text-white text-sm rounded hover:bg-cyan-600 disabled:opacity-50" data-testid="notifications-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : 'Update'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ============ Shared FormRow ============
function FormRow({ label, children }) {
  return (
    <div className="flex items-center gap-4 py-2">
      <label className="w-44 text-right text-sm text-slate-600 flex-shrink-0">{label}</label>
      {children}
    </div>
  );
}

// ============ Tab 6: Quote Note Templates ============
function QuoteNoteTemplatesTab() {
  const queryClient = useQueryClient();
  const { data: templates = [], isLoading } = useQuery({ queryKey: ['quoteNoteTemplates'], queryFn: () => getQuoteNoteTemplates().then(r => r.data) });
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', body: '' });
  const [adding, setAdding] = useState(false);
  const [editorReady, setEditorReady] = useState(false);

  useEffect(() => { const t = setTimeout(() => setEditorReady(true), 100); return () => clearTimeout(t); }, []);

  const createMut = useMutation({
    mutationFn: (data) => createQuoteNoteTemplate(data),
    onSuccess: () => { toast.success('Template created'); queryClient.invalidateQueries({ queryKey: ['quoteNoteTemplates'] }); setAdding(false); setForm({ title: '', body: '' }); },
    onError: () => toast.error('Failed to create template')
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => updateQuoteNoteTemplate(id, data),
    onSuccess: () => { toast.success('Template updated'); queryClient.invalidateQueries({ queryKey: ['quoteNoteTemplates'] }); setEditingId(null); },
    onError: () => toast.error('Failed to update template')
  });
  const deleteMut = useMutation({
    mutationFn: (id) => deleteQuoteNoteTemplate(id),
    onSuccess: () => { toast.success('Template deleted'); queryClient.invalidateQueries({ queryKey: ['quoteNoteTemplates'] }); },
    onError: () => toast.error('Failed to delete template')
  });

  const startEdit = (t) => { setEditingId(t.id); setForm({ title: t.title, body: t.body }); setAdding(false); };
  const cancelEdit = () => { setEditingId(null); setForm({ title: '', body: '' }); };
  const startAdd = () => { setAdding(true); setEditingId(null); setForm({ title: '', body: '' }); };

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-primary" /></div>;

  return (
    <div data-testid="quote-note-templates-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-700">Quote Note Templates</h3>
          <p className="text-sm text-slate-500 mt-1">Manage reusable note templates that can be applied to quotes.</p>
        </div>
        {!adding && !editingId && (
          <button onClick={startAdd} className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white text-sm rounded hover:bg-cyan-600" data-testid="add-template-btn">
            <Plus size={16} /> Add Template
          </button>
        )}
      </div>

      {/* Add / Edit Form */}
      {(adding || editingId) && (
        <div className="bg-slate-50 border rounded-lg p-5 mb-6" data-testid="template-form">
          <h4 className="font-medium text-slate-700 mb-4">{adding ? 'New Template' : 'Edit Template'}</h4>
          <div className="mb-4">
            <label className="block text-sm text-slate-600 mb-1">Title</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm" placeholder="e.g. Default Roofing Notes" data-testid="template-title-input" />
          </div>
          <div className="mb-4">
            <label className="block text-sm text-slate-600 mb-1">Body</label>
            {editorReady && <RichTextEditor key={editingId || 'new'} value={form.body} onChange={val => setForm(prev => ({ ...prev, body: val }))} />}
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { cancelEdit(); setAdding(false); }} className="px-4 py-2 text-sm text-slate-600 border rounded hover:bg-slate-100" data-testid="template-cancel-btn">
              <X size={14} className="inline mr-1" />Cancel
            </button>
            <button
              onClick={() => adding ? createMut.mutate(form) : updateMut.mutate({ id: editingId, data: form })}
              disabled={!form.title.trim() || createMut.isPending || updateMut.isPending}
              className="px-4 py-2 text-sm bg-cyan-500 text-white rounded hover:bg-cyan-600 disabled:opacity-50" data-testid="template-save-btn">
              {(createMut.isPending || updateMut.isPending) ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} className="inline mr-1" />{adding ? 'Create' : 'Save'}</>}
            </button>
          </div>
        </div>
      )}

      {/* Template List */}
      {templates.length === 0 && !adding ? (
        <div className="text-center py-8 text-slate-400" data-testid="no-templates">No templates yet. Click "Add Template" to create one.</div>
      ) : (
        <div className="space-y-3">
          {templates.map(t => (
            <div key={t.id} className={`border rounded-lg p-4 ${editingId === t.id ? 'border-cyan-300 bg-cyan-50/30' : 'bg-white hover:shadow-sm'}`} data-testid={`template-${t.id}`}>
              {editingId !== t.id && (
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-800" data-testid={`template-title-${t.id}`}>{t.title}</h4>
                    <div className="text-sm text-slate-500 mt-1 line-clamp-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t.body?.substring(0, 200) + (t.body?.length > 200 ? '...' : '')) }} />
                  </div>
                  <div className="flex gap-1 ml-3 flex-shrink-0">
                    <button onClick={() => startEdit(t)} className="p-2 text-slate-400 hover:text-cyan-600 rounded hover:bg-slate-100" data-testid={`edit-template-${t.id}`}><Edit size={16} /></button>
                    <button onClick={() => { if (window.confirm(`Delete "${t.title}"?`)) deleteMut.mutate(t.id); }} className="p-2 text-slate-400 hover:text-red-500 rounded hover:bg-slate-100" data-testid={`delete-template-${t.id}`}><Trash2 size={16} /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ============ Tab: Send Log (outbound audit trail) ============
function SendLogTab() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['email-log'],
    queryFn: () => getEmailLog(100).then(r => r.data),
  });
  const items = data?.items || [];
  return (
    <div className="max-w-5xl" data-testid="send-log-tab">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Outbound Email Log</h3>
          <p className="text-xs text-slate-500 mt-0.5">Most recent {items.length} emails the app attempted to send. Kept for 180 days.</p>
        </div>
        <button onClick={() => refetch()} disabled={isFetching} className="px-3 py-1.5 text-sm border rounded hover:bg-slate-50 flex items-center gap-2" data-testid="refresh-send-log">
          {isFetching && <Loader2 size={14} className="animate-spin" />} Refresh
        </button>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-500 py-12 text-center border rounded bg-slate-50">No emails have been sent through the app yet.</div>
      ) : (
        <div className="overflow-x-auto border rounded">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs text-slate-600">
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">To</th>
                <th className="px-3 py-2 font-medium">Cc</th>
                <th className="px-3 py-2 font-medium">Bcc</th>
                <th className="px-3 py-2 font-medium">Subject</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((log) => (
                <tr key={log.id} className="border-t align-top">
                  <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-xs break-all">{(log.to || []).join(', ')}</td>
                  <td className="px-3 py-2 text-xs break-all text-slate-500">{(log.cc || []).join(', ') || '—'}</td>
                  <td className="px-3 py-2 text-xs break-all text-slate-500">{(log.bcc || []).join(', ') || '—'}</td>
                  <td className="px-3 py-2 text-xs max-w-xs truncate" title={log.subject}>{log.subject}</td>
                  <td className="px-3 py-2 text-xs">
                    {log.status === 'sent' ? (
                      <span className="inline-flex items-center gap-1 text-green-700"><Check size={12} /> Sent</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-700" title={log.error || ''}>
                        <X size={12} /> Failed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
